import React, { useEffect, useRef, useState, useCallback } from 'react';

import { 
  Character,
  CharacterFeature,
  CharacterASIBaseFeature,
  ASIBaseFeature,
  ASIFeature,
  CharacterASIFeature,
  CharacterSheetInterface,
} from "@dndModels";
import { AbilityScoreType } from "@dndModels/Types";
import { ABILITY_SCORES } from "@dndModels/Constants";

import CommonUtilities from "@utilities/common_utilities";

import SelectStringBox from "@components/input/selects/SelectStringBox";
import LoadingBox from "@components/input/LoadingBox";

interface Props {
  char: CharacterSheetInterface;
  obj: CharacterFeature;
  character_asi_base_feature: CharacterASIBaseFeature;
  asi_base_feature: ASIBaseFeature;
  character_asi_feature: CharacterASIFeature;
  asi_feature: ASIFeature;
  flex_mode: boolean;
}

const common_util = CommonUtilities.getInstance();

const old_options_map: { [key: string]: string[] } = {};

export default function CharacterASIFeatureInput({ 
  char,
  obj,
  character_asi_base_feature,
  asi_base_feature,
  character_asi_feature,
  asi_feature,
  flex_mode,
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [recalc, setRecalc] = useState(false);
  const [ping, setPing] = useState(false);
  const [options, setOptions] = useState<string[]>([]);
  const [prev_flex_mode, setPrevFlexMode] = useState(flex_mode);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!fetched) {
      load();
    }
  });

  const statsChanged = () => {
    setRecalc(true);
  }

  const calcOptions = useCallback(() => {
    if (obj.floating_feature instanceof ASIBaseFeature) {
      // console.log(asi_base_feature);
      let new_options = !flex_mode && asi_feature.options.length > 0 ? [...asi_feature.options] : [...ABILITY_SCORES];
      let i = 0; 
      while (i < new_options.length) {
        // Remove options that would put it over max.
        const opt = new_options[i];
        if (character_asi_feature.selected_option === opt) {
          ++i;
        } else if (ABILITY_SCORES.includes(opt)) {
          const max = char instanceof Character ? char.max_ability_scores.getAbilityScore(opt) : 50;
          const current = char.mind.current_ability_scores.getAbilityScore(opt);
          if (current + asi_feature.amount > max) {
            new_options = new_options.filter(o => o !== opt);
          } else {
            ++i;
          }
        } else {
          ++i;
        }
      }
      if (new_options.length === 0) {
        new_options = [...ABILITY_SCORES];
        i = 0; 
        while (i < new_options.length) {
          // Remove options that would put it over max.
          const opt = new_options[i];
          if (character_asi_feature.selected_option === opt) {
            ++i;
          } else if (ABILITY_SCORES.includes(opt)) {
            const max = char instanceof Character ? char.max_ability_scores.getAbilityScore(opt) : 50;
            const current = char.mind.current_ability_scores.getAbilityScore(opt);
            if (current + asi_feature.amount > max) {
              new_options = new_options.filter(o => o !== opt);
            } else {
              ++i;
            }
          } else {
            ++i;
          }
        }
      }
      const old_options = old_options_map[character_asi_feature.true_id];
      // console.log("new_options", new_options);
      // console.log("options", old_options);
      if (!asi_base_feature.allow_duplicates) {
        const other_selected_options = character_asi_base_feature.asi_features.filter(o => 
          o.selected_option !== "None" && o.true_id !== character_asi_feature.true_id).map(o => o.selected_option) as string[];
          new_options = new_options.filter(opt => !other_selected_options.includes(opt));
      }
      if (!old_options || !common_util.listsMatch(new_options, old_options)) {
        console.log("setting options");
        setOptions(new_options);
        old_options_map[character_asi_feature.true_id] = new_options;
      }
    }
  }, [character_asi_feature, character_asi_base_feature, asi_base_feature, asi_feature, char, obj, flex_mode, setOptions]);

  useEffect(() => {
    if (recalc) {
      setRecalc(false);
      calcOptions();
    }
  }, [recalc, calcOptions]);

  useEffect(() => {
    if (flex_mode !== prev_flex_mode) {
      setPrevFlexMode(flex_mode);
      setRecalc(true);
    }
  }, [flex_mode, prev_flex_mode, calcOptions]);

  const load = () => {
    console.log("load");
    delete old_options_map[character_asi_feature.true_id];
    setFetched(true);
    char.subscription_holder.subscribe("Stats", mounted, statsChanged);
    calcOptions();
  }

  // console.log(options);
  if (!fetched) {
    return (<LoadingBox />);
  } else if (obj.feature && obj.floating_feature) {
    if (options.length > 1) {
      return (
        <SelectStringBox
          name={`Ability Score Improvement by ${asi_feature.amount}`}
          options={options}
          value={character_asi_feature.selected_option}
          onChange={(changed: AbilityScoreType) => {
            character_asi_feature.selected_option = changed;
            setPing(!ping);
            char.changed();
            char.subscription_holder.recalculate();
          }}
          color={ character_asi_feature.selected_option === "None" ? "blue" : "" }
        />
      );
    } else if (options.length === 1) {
      return (
        <>
          {`${options[0]} increased ${asi_feature.amount}`}
        </>
      );
    }
  }
  return <></>;
}
